import { authClientWeb } from "react-redux-django-auth/web";

export interface DynamicServiceBreakdown {
  service_code: string;
  service_name: string;
  count: number;
  approved_count?: number;
  gross_revenue?: string;
  direct_cost?: string;
  total_commission: string;
  paid_commission?: string;
  pending_commission?: string;
  profit?: string;
  margin_percent?: number;
}

export interface StaffDeliverableItem {
  id: number;
  date: string;
  customer_name: string;
  service_type: string;
  service_name: string;
  amount_paid: string;
  commission: string;
  is_approved: boolean;
  is_commission_paid: boolean;
  status: string;
  is_split?: boolean;
  split_percentage?: string;
  stage_status?: string;
}

export interface StaffFinanceOverview {
  month: string;
  total_deliverables: number;
  completed_deliverables: number;
  pending_deliverables: number;
  approved_deliverables: number;
  total_earned: string;
  total_paid: string;
  pending_payout: string;
  awaiting_approval_count: number;
  lifetime_paid: string;
  service_breakdown: DynamicServiceBreakdown[];
  itemized_deliverables: StaffDeliverableItem[];
  available_months: string[];
}

export interface StaffPayrollRosterItem {
  staff_id: number;
  staff_name: string;
  staff_email: string;
  total_deliverables: number;
  approved_deliverables_count: number;
  services_handled: string[];
  services_handled_codes: string[];
  total_earned: string;
  total_paid: string;
  pending_payout: string;
  payout_status: 'FULLY_PAID' | 'PENDING_PAYOUT' | 'NO_COMMISSION';
  unpaid_ids: number[];
}

export interface SuperAdminFinanceSummary {
  gross_revenue: string;
  total_costs: string;
  platform_margin: string;
  total_deliverables: number;
  pending_deliverables: number;
  approved_deliverables: number;
  total_earned: string;
  total_paid: string;
  total_pending_payout: string;
  lifetime_total_disbursed: string;
  active_staff_count: number;
}

export interface SuperAdminFinanceOverview {
  month: string;
  summary: SuperAdminFinanceSummary;
  service_breakdown: DynamicServiceBreakdown[];
  staff_roster: StaffPayrollRosterItem[];
  available_months: string[];
}

export interface DisburseCommissionPayload {
  month?: string;
  staff_id?: number;
  job_ids?: number[];
  all_approved?: boolean;
  payment_method?: string;
  reference?: string;
}

export interface DisburseResponse {
  detail: string;
  count: number;
  total_paid: string;
  month?: string;
  payment_method?: string;
  reference?: string;
}

const getClient = () => {
  try {
    const client = authClientWeb();
    if (client) return client;
  } catch {
    // fallback if authClientWeb not initialized
  }
};

/**
 * Fetch staff personal finance overview and itemized deliverables
 */
export const fetchStaffFinanceOverview = async (month?: string): Promise<StaffFinanceOverview> => {
  const client = getClient();
  if (!client) throw new Error("API client not initialized");

  const params = new URLSearchParams();
  params.append("scope", "personal");
  if (month) params.append("month", month);

  const res = await client.get(`/jobs/monthly-payout/?${params.toString()}`);
  return res.data;
};

/**
 * Fetch SuperAdmin master platform treasury, multi-service matrix, and staff payroll roster
 */
export const fetchSuperAdminFinanceOverview = async (month?: string, signal?: AbortSignal): Promise<SuperAdminFinanceOverview> => {
  const client = getClient();
  if (!client) throw new Error("API client not initialized");

  const url = month ? `/jobs/monthly-payout/?month=${encodeURIComponent(month)}` : "/jobs/monthly-payout/";
  const res = await client.get(url, { signal });
  return res.data;
};

/**
 * Execute single-staff, selected-record, or batch monthly commission disbursement
 */
export const executeCommissionDisbursement = async (payload: DisburseCommissionPayload): Promise<DisburseResponse> => {
  const client = getClient();
  if (!client) throw new Error("API client not initialized");

  const res = await client.post("/jobs/monthly-payout/", payload);
  return res.data;
};

// Aliases for convenience & backward compatibility
export const fetchCompanyFinanceOverview = fetchSuperAdminFinanceOverview;
export const fetchFinanceSummary = fetchSuperAdminFinanceOverview;
export const fetchPayrollRoster = async (month?: string): Promise<StaffPayrollRosterItem[]> => {
  const overview = await fetchSuperAdminFinanceOverview(month);
  return overview?.staff_roster ?? [];
};

